import { Box, BoxProps, Button, ButtonProps } from "@mui/material";
import React, { ReactNode } from "react";
import { CHART_COLORS, CHART_DIMENSIONS, CHART_TYPOGRAPHY } from "./tokens";

interface ChartActionStripProps {
  /** Content rendered on the left side (e.g. sub-tabs, labels) */
  startContent?: ReactNode;
  /** Content rendered on the right side (e.g. action buttons) */
  endContent?: ReactNode;
  /** Override container styles */
  sx?: BoxProps["sx"];
}

/**
 * Reusable blue action strip that appears below patient chart tabs.
 *
 * Usage examples:
 * - Visit History: endContent={<ChartActionStrip.Button>Create Visit Note</ChartActionStrip.Button>}
 * - Order: startContent={sub-tabs} endContent={multiple action buttons}
 * - Chronological: endContent={search + date picker + button}
 */
const ChartActionStrip: React.FC<ChartActionStripProps> & {
  Button: React.FC<ChartStripButtonProps>;
} = ({ startContent, endContent, sx }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: CHART_COLORS.bgSecondaryBlue,
        borderBottom: `1px solid ${CHART_COLORS.border}`,
        height: CHART_DIMENSIONS.tertiaryNavHeight,
        minHeight: CHART_DIMENSIONS.tertiaryNavHeight,
        maxHeight: CHART_DIMENSIONS.tertiaryNavHeight,
        px: 1.5,
        gap: 1.5,
        flexShrink: 0,
        ...sx,
      }}
    >
      {startContent && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {startContent}
        </Box>
      )}

      {!startContent && (
        <Box
          sx={{
            flex: 1,
          }}
        />
      )}

      {endContent && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {endContent}
        </Box>
      )}
    </Box>
  );
};

type ChartStripButtonProps = {
  variant?: ButtonProps["variant"];
  onClick?: () => void;
  children: ReactNode;
  startIcon?: ReactNode;
  disabled?: boolean;
};

const ChartStripButton: React.FC<ChartStripButtonProps> = ({
  variant = "contained",
  onClick,
  children,
  startIcon,
  disabled,
}) => (
  <Button
    variant={variant}
    onClick={onClick}
    startIcon={startIcon}
    disabled={disabled}
    disableElevation
    sx={{
      bgcolor: variant === "contained" ? CHART_COLORS.primary : "transparent",
      color:
        variant === "contained" ? CHART_COLORS.textLight : CHART_COLORS.primary,
      border:
        variant === "outlined"
          ? `1px solid ${CHART_COLORS.primary}`
          : undefined,
      height: CHART_DIMENSIONS.controlHeight,
      borderRadius: CHART_DIMENSIONS.borderRadius,
      ...CHART_TYPOGRAPHY.body5,
      fontFamily: CHART_TYPOGRAPHY.fontFamily,
      fontWeight: CHART_TYPOGRAPHY.weight.regular,
      textTransform: "none",
      whiteSpace: "nowrap",
      px: 1.5,
      "&:hover": {
        bgcolor:
          variant === "contained"
            ? CHART_COLORS.primaryPressed
            : CHART_COLORS.bgSecondaryHover,
      },
      "&:active": {
        bgcolor:
          variant === "contained"
            ? CHART_COLORS.primaryHover
            : CHART_COLORS.bgSecondaryHover,
      },
      "& .MuiButton-startIcon": {
        mr: "4px",
        "& svg": { fontSize: CHART_DIMENSIONS.iconMd },
      },
    }}
  >
    {children}
  </Button>
);

ChartActionStrip.Button = ChartStripButton;

export default ChartActionStrip;
